import { useState } from "react";
import { type UserStory } from "@/lib/projectStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, GripVertical, X } from "lucide-react";

const priorityColors: Record<UserStory["priority"], string> = {
  must: "bg-destructive/15 text-destructive border-destructive/30",
  should: "bg-accent/15 text-accent border-accent/30",
  could: "bg-secondary text-secondary-foreground border-border",
  wont: "bg-muted text-muted-foreground border-border",
};

const priorityLabels: Record<UserStory["priority"], string> = {
  must: "Must Have",
  should: "Should Have",
  could: "Could Have",
  wont: "Won't Have",
};

const statusColors: Record<UserStory["status"], string> = {
  backlog: "bg-muted text-muted-foreground",
  "in-progress": "bg-accent/15 text-accent",
  done: "bg-primary/15 text-primary",
};

interface Props {
  stories: UserStory[];
  onChange: (stories: UserStory[]) => void;
}

export default function UserStoryEditor({ stories, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: "",
    asA: "",
    iWant: "",
    soThat: "",
    priority: "should" as UserStory["priority"],
    storyPoints: 3,
    criteria: "",
  });

  const handleAdd = () => {
    if (!form.title.trim()) return;
    const story: UserStory = {
      id: crypto.randomUUID(),
      title: form.title.trim(),
      asA: form.asA.trim(),
      iWant: form.iWant.trim(),
      soThat: form.soThat.trim(),
      acceptanceCriteria: form.criteria
        .split("\n")
        .map((c) => c.trim())
        .filter(Boolean),
      priority: form.priority,
      storyPoints: form.storyPoints,
      status: "backlog",
    };
    onChange([...stories, story]);
    setForm({ title: "", asA: "", iWant: "", soThat: "", priority: "should", storyPoints: 3, criteria: "" });
    setOpen(false);
  };

  const updateStoryStatus = (id: string, status: UserStory["status"]) => {
    onChange(stories.map((s) => (s.id === id ? { ...s, status } : s)));
  };

  const removeStory = (id: string) => {
    onChange(stories.filter((s) => s.id !== id));
  };

  const grouped = {
    must: stories.filter((s) => s.priority === "must"),
    should: stories.filter((s) => s.priority === "should"),
    could: stories.filter((s) => s.priority === "could"),
    wont: stories.filter((s) => s.priority === "wont"),
  };

  return (
    <div className="p-4 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-mono text-muted-foreground uppercase tracking-wider">
            User Stories
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            MoSCoW prioritized · {stories.length} total ·{" "}
            {stories.reduce((a, s) => a + (s.storyPoints || 0), 0)} pts
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="gap-1.5">
              <Plus className="h-3.5 w-3.5" /> Add Story
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display">New User Story</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 pt-2">
              <Input
                placeholder="Story title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
              <div className="grid grid-cols-3 gap-2">
                <Input
                  placeholder="As a…"
                  value={form.asA}
                  onChange={(e) => setForm({ ...form, asA: e.target.value })}
                />
                <Input
                  placeholder="I want…"
                  value={form.iWant}
                  onChange={(e) => setForm({ ...form, iWant: e.target.value })}
                />
                <Input
                  placeholder="So that…"
                  value={form.soThat}
                  onChange={(e) => setForm({ ...form, soThat: e.target.value })}
                />
              </div>
              <Textarea
                placeholder="Acceptance criteria (one per line)"
                value={form.criteria}
                onChange={(e) => setForm({ ...form, criteria: e.target.value })}
                rows={3}
              />
              <div className="flex gap-3">
                <Select
                  value={form.priority}
                  onValueChange={(v) => setForm({ ...form, priority: v as any })}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="must">Must Have</SelectItem>
                    <SelectItem value="should">Should Have</SelectItem>
                    <SelectItem value="could">Could Have</SelectItem>
                    <SelectItem value="wont">Won't Have</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  min={1}
                  max={21}
                  value={form.storyPoints}
                  onChange={(e) => setForm({ ...form, storyPoints: +e.target.value })}
                  className="w-20"
                  placeholder="Pts"
                />
              </div>
              <Button onClick={handleAdd} className="w-full">
                Add Story
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {stories.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">
          No user stories yet. Click "Add Story" to create your first one.
        </div>
      ) : (
        Object.entries(grouped).map(([priority, items]) => {
          if (items.length === 0) return null;
          return (
            <div key={priority}>
              <h3 className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2">
                <Badge variant="outline" className={priorityColors[priority as UserStory["priority"]]}>
                  {priorityLabels[priority as UserStory["priority"]]}
                </Badge>
                <span className="text-muted-foreground/50">({items.length})</span>
              </h3>
              <div className="space-y-2">
                {items.map((story) => (
                  <div
                    key={story.id}
                    className="border border-border rounded-md p-3 bg-card flex items-start gap-3"
                  >
                    <GripVertical className="h-4 w-4 text-muted-foreground/30 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold text-card-foreground font-body">
                          {story.title}
                        </span>
                        {story.storyPoints && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                            {story.storyPoints} pts
                          </Badge>
                        )}
                      </div>
                      {story.asA && (
                        <p className="text-xs text-muted-foreground font-body">
                          As a <span className="font-semibold">{story.asA}</span>, I want{" "}
                          <span className="font-semibold">{story.iWant}</span>, so that{" "}
                          <span className="font-semibold">{story.soThat}</span>
                        </p>
                      )}
                      {story.acceptanceCriteria.length > 0 && (
                        <div className="mt-1.5 space-y-0.5">
                          {story.acceptanceCriteria.map((c, i) => (
                            <p key={i} className="text-[11px] text-muted-foreground font-mono">
                              ✓ {c}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Select
                        value={story.status}
                        onValueChange={(v) => updateStoryStatus(story.id, v as any)}
                      >
                        <SelectTrigger className="h-7 text-[10px] w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="backlog">Backlog</SelectItem>
                          <SelectItem value="in-progress">In Progress</SelectItem>
                          <SelectItem value="done">Done</SelectItem>
                        </SelectContent>
                      </Select>
                      <button
                        onClick={() => removeStory(story.id)}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
